import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { CheckCircle2, ChevronLeft, Info, Landmark, Loader2, MapPin, Search } from "lucide-react";
import { API } from "@/constants";
import Disclaimer from "@/components/shared/Disclaimer";
import Footer from "@/components/shared/Footer";
import { useEngagementTracker } from "@/hooks/useEngagementTracker";

const VenueComparisonPage = () => {
  const [venueData, setVenueData] = useState(null);
  const [selectedVenues, setSelectedVenues] = useState([]);
  const [comparison, setComparison] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("federal");
  const [selectedState, setSelectedState] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const { trackEvent } = useEngagementTracker();
  
  useEffect(() => {
    trackEvent('venue', 'view');
    axios.get(`${API}/venue/list`).then(r => {
      setVenueData(r.data);
      if (r.data.states_list && r.data.states_list.length > 0) {
        setSelectedState(r.data.states_list[0]);
      }
    }).finally(() => setLoading(false));
  }, [trackEvent]);
  
  const toggleVenue = (venueId) => {
    if (selectedVenues.includes(venueId)) {
      setSelectedVenues(selectedVenues.filter(v => v !== venueId));
    } else if (selectedVenues.length < 4) {
      setSelectedVenues([...selectedVenues, venueId]);
    }
  };
  
  const compare = async () => {
    if (selectedVenues.length < 2) return;
    try {
      const res = await axios.post(`${API}/venue/compare`, { venues: selectedVenues });
      setComparison(res.data);
      // Track venue comparison - high-value action
      trackEvent('venue', 'venue_compared', { count: selectedVenues.length });
    } catch (e) {
      console.error(e);
    }
  };
  
  const getFilteredVenues = () => {
    if (!venueData) return [];
    
    let venues = [];
    if (activeTab === "federal") {
      venues = venueData.federal_courts || [];
    } else if (selectedState && venueData.state_courts) {
      venues = venueData.state_courts[selectedState] || [];
    }
    
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      venues = venues.filter(v => 
        v.name.toLowerCase().includes(term) || 
        v.city?.toLowerCase().includes(term) ||
        v.state?.toLowerCase().includes(term)
      );
    }
    
    return venues;
  };
  
  const filteredVenues = getFilteredVenues();
  
  return (
    <main className="min-h-screen bg-slate-50" data-testid="venue-comparison-page">
      <Disclaimer />
      <div className="container-shell py-8">
        <div className="mb-6">
          <Link to="/tools" className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1 mb-4">
            <ChevronLeft className="h-4 w-4" /> Back to Tools
          </Link>
          <h1 className="text-3xl font-bold" style={{ fontFamily: "'Fraunces', serif" }}>Venue Comparison</h1>
          <p className="text-muted-foreground">Compare court statistics and benchmarks across all 50 states and federal districts</p>
        </div>
        
        {/* Summary Stats */}
        {venueData?.summary && (
          <div className="grid gap-4 md:grid-cols-4 mb-6">
            <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white border-0">
              <CardContent className="p-4">
                <p className="text-blue-100 text-sm">Total Venues</p>
                <p className="text-3xl font-bold">{venueData.summary.total_venues}</p>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-indigo-500 to-indigo-600 text-white border-0">
              <CardContent className="p-4">
                <p className="text-indigo-100 text-sm">Federal Districts</p>
                <p className="text-3xl font-bold">{venueData.summary.federal_districts}</p>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white border-0">
              <CardContent className="p-4">
                <p className="text-purple-100 text-sm">State Courts</p>
                <p className="text-3xl font-bold">{venueData.summary.state_courts}</p>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-emerald-500 to-emerald-600 text-white border-0">
              <CardContent className="p-4">
                <p className="text-emerald-100 text-sm">States Covered</p>
                <p className="text-3xl font-bold">{venueData.summary.states_covered}</p>
              </CardContent>
            </Card>
          </div>
        )}
        
        {/* Selected Venues Bar */}
        {selectedVenues.length > 0 && (
          <Card className="mb-6 bg-primary/5 border-primary/20">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Badge className="bg-primary">{selectedVenues.length} Selected</Badge>
                  <span className="text-sm text-muted-foreground">Select 2-4 venues to compare</span>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => setSelectedVenues([])}>
                    Clear All
                  </Button>
                  <Button size="sm" onClick={compare} disabled={selectedVenues.length < 2}>
                    <Landmark className="h-4 w-4 mr-2" />
                    Compare Venues
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
        
        <Card className="mb-6">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Browse Courts</CardTitle>
                <CardDescription>Select venues from federal districts or state courts</CardDescription>
              </div>
              <div className="relative w-64">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input 
                  placeholder="Search courts..." 
                  className="pl-9"
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : (
              <div className="space-y-4">
                <Tabs value={activeTab} onValueChange={setActiveTab}>
                  <TabsList className="w-full justify-start">
                    <TabsTrigger value="federal" className="gap-2">
                      <Landmark className="h-4 w-4" /> Federal Courts ({venueData?.summary?.federal_districts || 0})
                    </TabsTrigger>
                    <TabsTrigger value="state" className="gap-2">
                      <MapPin className="h-4 w-4" /> State Courts ({venueData?.summary?.state_courts || 0})
                    </TabsTrigger>
                  </TabsList>
                  
                  <TabsContent value="federal" className="mt-4">
                    <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3 max-h-[500px] overflow-y-auto p-1">
                      {filteredVenues.map(venue => (
                        <button
                          key={venue.id}
                          onClick={() => toggleVenue(venue.id)}
                          className={`p-4 rounded-xl border text-left transition-all ${
                            selectedVenues.includes(venue.id) 
                              ? 'border-primary bg-primary/5 ring-2 ring-primary' 
                              : 'hover:border-slate-300 hover:bg-slate-50'
                          }`}
                        >
                          <div className="flex items-center justify-between mb-2">
                            <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200">
                              {venue.state}
                            </Badge>
                            {selectedVenues.includes(venue.id) && (
                              <CheckCircle2 className="h-4 w-4 text-primary" />
                            )}
                          </div>
                          <h4 className="font-medium text-sm">{venue.name}</h4>
                          <p className="text-xs text-muted-foreground mt-1">{venue.city}</p>
                          <div className="flex gap-3 mt-2 text-xs">
                            <span className="text-blue-600">{venue.avg_time_to_trial} mo</span>
                            <span className="text-emerald-600">{(venue.settlement_rate * 100).toFixed(0)}% settle</span>
                            <span className="text-slate-500">${venue.filing_fee}</span>
                          </div>
                        </button>
                      ))}
                    </div>
                  </TabsContent>
                  
                  <TabsContent value="state" className="mt-4">
                    <div className="flex gap-4">
                      {/* State Selector */}
                      <div className="w-48 shrink-0">
                        <Label className="text-sm font-medium mb-2 block">Select State</Label>
                        <Select value={selectedState} onValueChange={setSelectedState}>
                          <SelectTrigger>
                            <SelectValue placeholder="Choose state" />
                          </SelectTrigger>
                          <SelectContent className="max-h-[300px]">
                            {venueData?.states_list?.map(state => (
                              <SelectItem key={state} value={state}>
                                {state} ({venueData.state_courts[state]?.length || 0})
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      
                      {/* State Courts Grid */}
                      <div className="flex-1 grid gap-3 md:grid-cols-2 lg:grid-cols-3 max-h-[500px] overflow-y-auto p-1">
                        {filteredVenues.map(venue => (
                          <button
                            key={venue.id}
                            onClick={() => toggleVenue(venue.id)}
                            className={`p-4 rounded-xl border text-left transition-all ${
                              selectedVenues.includes(venue.id) 
                                ? 'border-primary bg-primary/5 ring-2 ring-primary' 
                                : 'hover:border-slate-300 hover:bg-slate-50'
                            }`}
                          >
                            <div className="flex items-center justify-between mb-2">
                              <Badge variant="outline" className="text-xs bg-purple-50 text-purple-700 border-purple-200">
                                {venue.city}
                              </Badge>
                              {selectedVenues.includes(venue.id) && (
                                <CheckCircle2 className="h-4 w-4 text-primary" />
                              )}
                            </div>
                            <h4 className="font-medium text-sm">{venue.name}</h4>
                            <div className="flex gap-3 mt-2 text-xs">
                              <span className="text-blue-600">{venue.avg_time_to_trial} mo</span>
                              <span className="text-emerald-600">{(venue.settlement_rate * 100).toFixed(0)}% settle</span>
                              <span className="text-slate-500">${venue.filing_fee}</span>
                            </div>
                          </button>
                        ))}
                        {filteredVenues.length === 0 && (
                          <div className="col-span-full text-center py-8 text-muted-foreground">
                            No courts found for {selectedState}
                          </div>
                        )}
                      </div>
                    </div>
                  </TabsContent>
                </Tabs>
              </div>
            )}
          </CardContent>
        </Card>
        
        {/* Comparison Results */}
        {comparison && (
          <div className="space-y-4 animate-fade-in">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold">Comparison Results</h2>
              <Button variant="outline" size="sm" onClick={() => setComparison(null)}>
                Clear Results
              </Button>
            </div>
            
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
              {comparison.comparison.map((venue, i) => (
                <Card key={i}>
                  <CardHeader className="pb-2">
                    <Badge className={`w-fit mb-2 ${venue.jurisdiction === 'federal' ? 'bg-blue-500' : 'bg-purple-500'}`}>
                      {venue.jurisdiction === 'federal' ? 'Federal' : venue.state}
                    </Badge>
                    <CardTitle className="text-base leading-tight">{venue.name}</CardTitle>
                    {venue.city && <p className="text-xs text-muted-foreground">{venue.city}</p>}
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="grid grid-cols-2 gap-2">
                      <div className="p-2 bg-slate-50 rounded-lg text-center">
                        <p className="text-xs text-muted-foreground">Time to Trial</p>
                        <p className="text-lg font-bold text-blue-600">{venue.avg_time_to_trial_months} mo</p>
                      </div>
                      <div className="p-2 bg-slate-50 rounded-lg text-center">
                        <p className="text-xs text-muted-foreground">Settlement</p>
                        <p className="text-lg font-bold text-emerald-600">{(venue.settlement_rate * 100).toFixed(0)}%</p>
                      </div>
                      <div className="p-2 bg-slate-50 rounded-lg text-center">
                        <p className="text-xs text-muted-foreground">Filing Fee</p>
                        <p className="text-lg font-bold">${venue.filing_fee}</p>
                      </div>
                      <div className="p-2 bg-slate-50 rounded-lg text-center">
                        <p className="text-xs text-muted-foreground">Judges</p>
                        <p className="text-lg font-bold">{venue.judge_count}</p>
                      </div>
                    </div>
                    
                    {venue.annual_filings && (
                      <div className="text-xs text-muted-foreground text-center">
                        ~{venue.annual_filings.toLocaleString()} annual filings
                      </div>
                    )}
                    
                    {venue.notes && venue.notes.length > 0 && (
                      <div className="pt-2 border-t">
                        <p className="text-xs font-medium mb-1">Notes</p>
                        <ul className="space-y-0.5">
                          {venue.notes.slice(0, 3).map((note, j) => (
                            <li key={j} className="text-xs text-muted-foreground flex gap-1">
                              <span>•</span> {note}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
            
            <Alert className="bg-slate-100 border-slate-200">
              <Info className="h-4 w-4" />
              <AlertDescription className="text-xs">
                {comparison.disclaimer}
              </AlertDescription>
            </Alert>
          </div>
        )}
      </div>
      <Footer />
    </main>
  );
};


export default VenueComparisonPage;
